import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Ticket, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { reportError } from "@/lib/error-reporting";

const codeSchema = z.object({
  code: z.string().trim().min(1, "Please enter your invite code").max(50),
});

type CodeFormData = z.infer<typeof codeSchema>;

interface InviteCodeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWaitlistClick?: () => void;
}

interface CodeValidationResult {
  valid: boolean;
  error?: "invalid_code" | "code_expired" | "code_exhausted";
  tier?: string;
  duration_days?: number;
}

const ERROR_MESSAGES: Record<string, string> = {
  invalid_code: "This code doesn't exist or has been deactivated.",
  code_expired: "This code has expired.",
  code_exhausted: "This code has reached its maximum number of uses.",
};

export function InviteCodeModal({ open, onOpenChange, onWaitlistClick }: InviteCodeModalProps) {
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<CodeValidationResult | null>(null);
  const [validCode, setValidCode] = useState("");

  const form = useForm<CodeFormData>({
    resolver: zodResolver(codeSchema),
    defaultValues: {
      code: "",
    },
  });

  const validateCode = async (code: string): Promise<CodeValidationResult> => {
    const normalizedCode = code.toUpperCase().trim();

    // Use secure RPC function instead of direct table query
    const { data, error } = await supabase.rpc('validate_invite_code', { p_code: normalizedCode });

    if (error) {
      reportError(error, 'invite-code', { action: 'validate-code' });
      return { valid: false, error: "invalid_code" };
    }

    // Type assertion for RPC response
    const result = data as { valid: boolean; error?: string; tier?: string; duration_days?: number } | null;

    if (!result?.valid) {
      return { 
        valid: false, 
        error: (result?.error as "invalid_code" | "code_expired" | "code_exhausted") || "invalid_code" 
      };
    }

    return {
      valid: true,
      tier: result.tier || "beta_pro",
      duration_days: result.duration_days || 180,
    };
  };

  const onSubmit = async (data: CodeFormData) => {
    setIsValidating(true);
    setValidationResult(null);

    try {
      const result = await validateCode(data.code);
      setValidationResult(result);

      if (result.valid) {
        setValidCode(data.code.toUpperCase().trim());
      }
    } catch (error) {
      reportError(error, 'invite-code', { action: 'submit' });
      setValidationResult({ valid: false, error: "invalid_code" });
    } finally {
      setIsValidating(false);
    }
  };

  const handleContinueToSignup = () => {
    onOpenChange(false);
    navigate(`/auth?code=${encodeURIComponent(validCode)}`);
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after animation
    setTimeout(() => {
      setValidationResult(null);
      setValidCode("");
      form.reset();
    }, 200);
  };

  const handleWaitlistClick = () => {
    handleClose();
    if (onWaitlistClick) {
      onWaitlistClick();
    }
  };

  const durationMonths = validationResult?.duration_days 
    ? Math.round(validationResult.duration_days / 30) 
    : 0;
  
  const isBetaPro = validationResult?.tier === 'beta_pro';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {!validationResult?.valid ? (
          <>
            <DialogHeader className="text-center space-y-3">
              <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <Ticket className="w-6 h-6 text-primary" />
              </div>
              <DialogTitle className="text-2xl font-bold">
                Enter Invite Code
              </DialogTitle>
              <p className="text-muted-foreground text-sm">
                Have an invite code from a friend or organization? 
                Enter it below to get immediate access.
              </p>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invite Code *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="BETA-XXXX-XXXX"
                          className="text-center font-mono text-lg uppercase tracking-wider"
                          {...field}
                          onChange={(e) => {
                            field.onChange(e.target.value.toUpperCase());
                            // Clear previous validation error when typing
                            if (validationResult) {
                              setValidationResult(null);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                      {validationResult?.error && (
                        <p className="text-sm text-destructive">
                          {ERROR_MESSAGES[validationResult.error]}
                        </p>
                      )}
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  disabled={isValidating}
                >
                  {isValidating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </Form>

            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-center text-muted-foreground">
                Don't have a code?{" "}
                <button
                  onClick={handleWaitlistClick}
                  className="text-primary hover:text-primary/80 underline underline-offset-2 font-medium"
                >
                  Join the Waitlist
                </button>{" "}
                instead
              </p>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="text-center space-y-3">
              <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-success/20 to-accent/20 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-success" />
              </div>
              <DialogTitle className="text-2xl font-bold">
                Code accepted!
              </DialogTitle>
              <p className="text-muted-foreground text-sm">
                {isBetaPro ? (
                  <>
                    You're getting <span className="font-semibold text-foreground">{durationMonths} months</span> of Pro access.
                  </>
                ) : (
                  <>You're all set to join!</>
                )}
                <br />
                Let's set up your account.
              </p>
            </DialogHeader>

            <div className="mt-6">
              <Button
                onClick={handleContinueToSignup}
                className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90"
              >
                Create Account
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
